import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AssessmentResult, type CheckStatus } from '../types/assessment';

interface Props {
  result: AssessmentResult;
  onClear: () => void;
}

const STATUS_ICONS: Record<CheckStatus, string> = {
  PASS: '✓',
  WARNING: '⚠',
  FAIL: '✗',
  NOT_ASSESSED: '?',
};

const STATUS_CLASSES: Record<CheckStatus, string> = {
  PASS: 'status-pass',
  WARNING: 'status-warn',
  FAIL: 'status-fail',
  NOT_ASSESSED: 'status-na',
};

// Helper to safely format numbers that might be null/undefined at runtime
function safeNum(value: number | null | undefined, fallback: number = 0): number {
  return typeof value === 'number' && !isNaN(value) ? value : fallback;
}

export default function AssessmentResults({ result, onClear }: Props) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CheckStatus | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  const filteredChecks = result.checks.filter(check => {
    if (filter !== 'ALL' && check.status !== filter) return false;
    if (categoryFilter !== 'ALL' && check.category !== categoryFilter) return false;
    return true;
  });

  const getScoreClass = (score: number) => {
    if (score >= 80) return 'score-high';
    if (score >= 60) return 'score-medium';
    return 'score-low';
  };

  const categories = [...new Set(result.checks.map(c => c.category))];

  // Defensive: coerce any potentially null numeric fields to zero before rendering
  const overallScore = safeNum(result.overallScore);
  const coverage = safeNum(result.coverage);
  const totalChecks = safeNum(result.summary?.totalChecks);
  const passCount = safeNum(result.summary?.passCount);
  const warningCount = safeNum(result.summary?.warningCount);
  const failCount = safeNum(result.summary?.failCount);

  return (
    <div>
      {/* Scoreboard */}
      <div className="scoreboard">
        <div className="score-card">
          <div className={`score-value ${getScoreClass(overallScore)}`}>
            {overallScore}
          </div>
          <div className="score-label">Overall Score</div>
        </div>
        <div className="score-card">
          <div className="score-value score-high">{passCount}</div>
          <div className="score-label">Passed</div>
        </div>
        <div className="score-card">
          <div className="score-value score-medium">{warningCount}</div>
          <div className="score-label">Warnings</div>
        </div>
        <div className="score-card">
          <div className="score-value score-low">{failCount}</div>
          <div className="score-label">Failed</div>
        </div>
        <div className="score-card">
          <div className="score-value">{coverage.toFixed(0)}%</div>
          <div className="score-label">Coverage</div>
        </div>
      </div>

      {/* Category Scores */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2>Category Scores</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Checks</th>
                <th>Pass</th>
                <th>Warning</th>
                <th>Fail</th>
                <th>Not Assessed</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {result.categoryScores.map(cat => (
                <tr key={cat.category}>
                  <td>{cat.category}</td>
                  <td>{safeNum(cat.totalChecks)}</td>
                  <td style={{ color: 'var(--color-success)', fontWeight: 600 }}>{safeNum(cat.passCount)}</td>
                  <td style={{ color: '#b06000', fontWeight: 600 }}>{safeNum(cat.warningCount)}</td>
                  <td style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{safeNum(cat.failCount)}</td>
                  <td>{safeNum(cat.notAssessedCount)}</td>
                  <td>
                    <span className={`status ${getScoreClass(safeNum(cat.score))}`} style={{ fontSize: '0.875rem' }}>
                      {safeNum(cat.score)}/100
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Findings */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Findings</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              style={{ padding: '0.375rem 0.75rem', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }}
            >
              <option value="ALL">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value as CheckStatus | 'ALL')}
              style={{ padding: '0.375rem 0.75rem', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="FAIL">Fail</option>
              <option value="WARNING">Warning</option>
              <option value="PASS">Pass</option>
              <option value="NOT_ASSESSED">Not Assessed</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
          Showing {filteredChecks.length} of {totalChecks} checks
        </div>

        {filteredChecks.map(check => (
          <div key={check.checkId} className={`finding-card severity-${check.status === 'FAIL' ? 'critical' : check.status === 'WARNING' ? 'high' : 'low'}`}>
            <div className="finding-header">
              <div>
                <span className="finding-title">{check.checkId}: {check.checkName}</span>
                <span style={{ marginLeft: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>
                  {check.category}
                </span>
              </div>
              <span className={`status ${STATUS_CLASSES[check.status]}`}>
                {STATUS_ICONS[check.status]} {check.status}
              </span>
            </div>
            <div className="finding-meta">
              <p><strong>Observed:</strong> {String(check.observedValue ?? 'N/A')} | <strong>Expected:</strong> {String(check.expectedValue ?? 'N/A')}</p>
              <p style={{ marginTop: '0.25rem' }}><strong>Finding:</strong> {check.finding}</p>
              <p style={{ marginTop: '0.25rem', color: 'var(--color-primary)' }}><strong>Recommendation:</strong> {check.recommendation}</p>
              <p style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                Source: {check.apiSource} | {new Date(check.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Limitations */}
      {result.limitations.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem', background: '#fff8e1' }}>
          <h2>Assessment Limitations</h2>
          <ul style={{ paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
            {result.limitations.map((limitation, idx) => (
              <li key={idx} style={{ marginBottom: '0.5rem' }}>{limitation}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="btn-group">
        <button className="btn btn-secondary" onClick={onClear}>
          Clear Session
        </button>
        <button className="btn btn-primary" onClick={() => navigate('/report')}>
          View Report
        </button>
      </div>
    </div>
  );
}
